import { useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useGetFailedDocs } from '@/api/queries/useGetFailedDocs';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { FailedDocument } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { formatFileSize } from '@/new/components/file-uploader/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/new/components/shadcn/dialog';
import { ScrollableTable } from '@/new/components/table/scrollable-table';

const fallbackTableData: FailedDocument[] = [];
const columnHelper = createColumnHelper<FailedDocument>();

const columns = [
  columnHelper.accessor('name', {
    header: () => <span>Name</span>,
    cell: (info) => <span className="text-gray-900">{info.getValue()}</span>,
  }),
  columnHelper.accessor('type', {
    header: () => <span>Type</span>,
    cell: (info) => <span className="text-gray-500">{info.getValue() || '—'}</span>,
  }),
  columnHelper.accessor('size', {
    header: () => <span>Size</span>,
    cell: (info) => <span className="text-gray-500">{formatFileSize(info.getValue() ?? 0)}</span>,
  }),
  columnHelper.accessor('uploadDate', {
    header: () => <span>Upload Date</span>,
    cell: (info) => <span className="text-gray-500">{formatDate(info.getValue(), true, true)}</span>,
  }),
  columnHelper.accessor('pipelineErrorType', {
    header: () => <span>Error Reason</span>,
    cell: (info) => <span className="text-red-600">{info.getValue()}</span>,
  }),
];

interface FailedDocsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  runId: string;
}

export function FailedDocsModal({ open, onOpenChange, matterId, runId }: FailedDocsModalProps) {
  const [globalFilter, setGlobalFilter] = useState('');
  const { data: runs } = useGetMatterRuns(matterId);
  const { data: failedDocs, isLoading } = useGetFailedDocs(runId);

  const selectedRun = runs?.find((r) => r.id === runId);

  const table = useReactTable({
    data: failedDocs ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] max-w-6xl flex-col">
        <DialogHeader>
          <DialogTitle>Failed Documents</DialogTitle>
          <DialogDescription>
            {selectedRun
              ? `Documents that failed to process in the run started ${formatDate(selectedRun.createdDate, true, true)}`
              : 'Documents that failed to process'}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-hidden">
          <ScrollableTable
            table={table}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            plural="failed documents"
            loading={isLoading}
            className="h-full max-h-[50vh] px-1 pt-1"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
